import { useGlobalStore } from '@/stores/global'
import { storeToRefs } from 'pinia'
import API from '@/services'
import type { MissionTrackEntry, UnitTrackPoint } from '@/types'

export function useUnitTracking() {
    const globals = useGlobalStore()
    const { missionEvents, viewCurrentMissionEvents, viewCurrentMissionPlan } = storeToRefs(globals)

    // Función para actualizar tracks de unidades activas
    const updateActiveUnitsTrack = (filteredUnitsOnline: any[]) => {
        filteredUnitsOnline.forEach(unit => {
            // Solo procesar si la unidad está en estado=1 (online) y tiene track visible
            if (unit.estado === 1 && globals.getUnitTrackVisibility(unit.unit_id)) {
                // Crear el punto de track con la ubicación actual
                const trackPoint: UnitTrackPoint = {
                    utc: new Date().toISOString(),
                    location: {
                        type: "Point",
                        coordinates: [unit.location.coordinates[0], unit.location.coordinates[1]]
                    }
                }

                // Agregar el punto al track de la unidad
                globals.addUnitTrackPoint(unit.unit_id, trackPoint)

                console.log(`📍 Track actualizado para ${unit.name} (${unit.unit_id})`)
            }
        })
    }

    const handleShowTrackChange = async (
        getMissionByUnitId: (unitId: string) => string | null,
        setCheckboxState: (unitId: string, checkboxType: any, value: boolean) => void,
        unitId: string | null,
        checked: boolean
    ) => {
        if (!unitId) return

        if (checked) {
            // Obtener el mission de la unidad
            const missionId = getMissionByUnitId(unitId)

            if (missionId) {
                try {
                    console.log(`🛰️ Cargando track para unidad ${unitId}, mission: ${missionId}`)

                    // Llamar al servicio getMissionTrack
                    const { status, data } = await API.getMissionTrack({
                        mission: parseInt(missionId),
                        unit_id: unitId,
                        distance_threshold: 5
                    })

                    if (status === 200 && data.Entries?.Entry) {
                        // Convertir la respuesta a UnitTrackPoint[]
                        const trackPoints: UnitTrackPoint[] = data.Entries.Entry.map((entry: MissionTrackEntry) => ({
                            utc: entry.utc,
                            location: JSON.parse(entry.location)
                        }))

                        // Almacenar en el store global
                        globals.initUnitTrack(unitId, trackPoints)
                        console.log(`✅ Track cargado para ${unitId}: ${trackPoints.length} puntos`)
                    } else {
                        console.warn(`⚠️ No se encontró track para la unidad ${unitId}`)
                    }
                } catch (error) {
                    console.error(`❌ Error cargando track para unidad ${unitId}:`, error)
                    // En caso de error, desactivar el checkbox
                    setCheckboxState(unitId, 'showTrack', false)
                }
            } else {
                console.error(`❌ No se pudo obtener mission para la unidad ${unitId}`)
                // En caso de no tener mission, desactivar el checkbox
                setCheckboxState(unitId, 'showTrack', false)
            }
        } else {
            // Si se desactiva, limpiar el track
            globals.clearUnitTrack(unitId)
        }

        globals.setUnitTrackVisibility(unitId, checked)
        console.log(`Show Track ${unitId}: ${checked}`)
    }

    // ========== FUNCIONES DE VERIFICACIÓN AUTOMÁTICA ==========

    const checkAndFollowUnits = (
        getCheckboxState: (unitId: string, checkboxType: any) => boolean,
        unitsOnline: any[],
        emit: any
    ) => {
        const followingUnits = Object.keys(globals.unitTracks || {}).filter(unitId =>
            getCheckboxState(unitId, 'seguir')
        )

        if (followingUnits.length > 0) {
            const unitToFollow = followingUnits[0]
            const unit = unitsOnline.find(u => u.unit_id === unitToFollow)

            if (unit && unit.location && unit.location.coordinates) {
                console.log(`🎯 Siguiendo unidad: ${unitToFollow} (${unit.name})`)
                console.log(`📍 Coordenadas: ${unit.location.coordinates}`)
                emit('unitSelected', unit.location.coordinates)
            } else {
                console.warn(`⚠️ No se encontraron coordenadas para la unidad ${unitToFollow}`)
            }

            if (followingUnits.length > 1) {
                console.warn(`⚠️ Múltiples unidades con seguir activo (${followingUnits.length}). Siguiendo: ${unitToFollow}`)
            }
        }
    }

    const checkAndUpdateMissionPlans = async (
        getCheckboxState: (unitId: string, checkboxType: any) => boolean,
        processMissionPlan: (unitId: string, enabled: boolean) => Promise<boolean>
    ) => {
        const missionPlanUnits = Object.keys(globals.unitTracks || {}).filter(unitId =>
            getCheckboxState(unitId, 'missionPlan')
        )

        if (missionPlanUnits.length > 0) {
            console.log(`📋 Actualizando Mission Plans para ${missionPlanUnits.length} unidad(es)`)

            await Promise.all(missionPlanUnits.map(unitId => processMissionPlan(unitId, true)))

            viewCurrentMissionPlan.value = true
        }
    }

    const checkAndUpdateMissionEvents = async (
        getCheckboxState: (unitId: string, checkboxType: any) => boolean,
        processEvents: (unitId: string, enabled: boolean) => Promise<any[]>
    ) => {
        const eventsUnits = Object.keys(globals.unitTracks || {}).filter(unitId =>
            getCheckboxState(unitId, 'events')
        )

        if (eventsUnits.length > 0) {
            console.log(`📅 Actualizando Mission Events para ${eventsUnits.length} unidad(es)`)

            const eventArrays = await Promise.all(eventsUnits.map(unitId => processEvents(unitId, true)))

            // Obtener el primer array de eventos que no esté vacío
            const events = eventArrays.find(arr => arr.length > 0) || []
            missionEvents.value = events
            viewCurrentMissionEvents.value = true
        }
    }

    const checkAndUpdateAllActiveStates = async (
        getCheckboxState: (unitId: string, checkboxType: any) => boolean,
        unitsOnline: any[],
        emit: any,
        filteredUnitsOnline: any[],
        processMissionPlan: (unitId: string, enabled: boolean) => Promise<boolean>,
        processEvents: (unitId: string, enabled: boolean) => Promise<any[]>
    ) => {
        checkAndFollowUnits(getCheckboxState, unitsOnline, emit)
        updateActiveUnitsTrack(filteredUnitsOnline)
        await Promise.all([
            checkAndUpdateMissionPlans(getCheckboxState, processMissionPlan),
            checkAndUpdateMissionEvents(getCheckboxState, processEvents),
        ])
    }

    return {
        updateActiveUnitsTrack,
        handleShowTrackChange,
        checkAndFollowUnits,
        checkAndUpdateMissionPlans,
        checkAndUpdateMissionEvents,
        checkAndUpdateAllActiveStates
    }
}
